import { useQuery } from "@tanstack/react-query";
import { useMemoizedFn } from "ahooks";
import log from "logbeacon";
import { wrapperResult } from "@api/osp";
import { QUERY_KEYS } from "../config";
import {
  Ecosystem,
  EcosystemPagination,
  type OpenDataClient,
} from "../features/api/openData";
import { useOspApiClientProvider } from "./useOspApiClientProvider";

type GetEcosystemRankingParams = NonNullable<
  Parameters<OpenDataClient<unknown>["ecosystems"]["getEcosystemRanking"]>[0]
>;

export interface UseEcosystemsListParams extends GetEcosystemRankingParams {
  // 可以扩展其他参数
}

export interface UseEcosystemsListResult {
  /** 生态系统列表数据 */
  data: Ecosystem[];
  /** 加载状态 */
  isLoading: boolean;
  /** 错误信息 */
  error: Error | null;
  /** 总数量 */
  total?: number;
  /** 分页偏移 */
  offset?: number;
  /** 刷新数据 */
  refetch: () => void;
}

/**
 * 用于获取生态系统排行榜的Hook
 * @param params 请求参数
 * @param options 查询选项
 * @returns 生态系统列表结果
 */
export const useEcosystemsList = (
  params?: UseEcosystemsListParams,
  options?: {
    enabled?: boolean;
  },
): UseEcosystemsListResult => {
  const { openDataApi } = useOspApiClientProvider();

  const {
    data: queryResult,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.ECOSYSTEMS_RANKING, params],
    queryFn: async (): Promise<EcosystemPagination> => {
      if (!openDataApi) {
        throw new Error("OpenData API client not available");
      }

      try {
        const requestParams: GetEcosystemRankingParams = {
          offset: params?.offset ?? 0,
          limit: params?.limit ?? 10,
          type: params?.type ?? "TVL",
          ...params,
        };

        log.debug("[文案]Getting ecosystems list with params:", requestParams);

        // 调用生成的API方法
        const response = await wrapperResult(
          openDataApi.ecosystems.getEcosystemRanking(requestParams),
        );
        return response as EcosystemPagination;
      } catch (error) {
        log.error("[文案]Get ecosystems list error:", error);
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!openDataApi,
  });

  const refetchData = useMemoizedFn(() => {
    refetch();
  });

  return {
    data: queryResult?.rows ?? [],
    isLoading,
    error,
    total: queryResult?.total,
    offset: queryResult?.offset,
    refetch: refetchData,
  };
};
